import React, { useState, useRef, useEffect } from 'react'
import { cn } from '@/utils/cn'

interface TutaraAutocompleteProps {
  label?: string
  suggestions?: string[]
  value?: string
  onChange?: (value: string) => void
  onSelect?: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  error?: string
  placeholder?: string
  className?: string
  'data-testid'?: string
}

export const TutaraAutocomplete = React.forwardRef<HTMLInputElement, TutaraAutocompleteProps>(
  ({ className, label, suggestions = [], value = '', onChange, onSelect, onKeyDown, error, placeholder, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(value)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([])
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const internalInputRef = useRef<HTMLInputElement>(null)
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalInputRef

    useEffect(() => {
      setInternalValue(value)
    }, [value])

    useEffect(() => {
      // Filter suggestions based on input
      if (internalValue && suggestions.length > 0) {
        const filtered = suggestions.filter(suggestion =>
          suggestion.toLowerCase().includes(internalValue.toLowerCase())
        )
        setFilteredSuggestions(filtered)
        setShowSuggestions(filtered.length > 0)
      } else {
        setFilteredSuggestions([])
        setShowSuggestions(false)
      }
    }, [internalValue, suggestions])

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setShowSuggestions(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInternalValue(newValue)
      onChange?.(newValue)
      setHighlightedIndex(-1)
    }

    const handleSuggestionClick = (suggestion: string) => {
      setInternalValue(suggestion)
      onChange?.(suggestion)
      onSelect?.(suggestion)
      setShowSuggestions(false)
      inputRef.current?.focus()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Call external onKeyDown handler first
      onKeyDown?.(e)

      if (!showSuggestions || filteredSuggestions.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex(prev =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1)
          break
        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
            handleSuggestionClick(filteredSuggestions[highlightedIndex])
          }
          break
        case 'Escape':
          setShowSuggestions(false)
          setHighlightedIndex(-1)
          break
      }
    }

    return (
      <div className="relative" ref={dropdownRef}>
        {label && (
          <label className="block text-sm font-medium text-tutara-charcoal dark:text-gray-200 mb-1">
            {label}
          </label>
        )}
        <input
          ref={inputRef}
          type="text"
          className={cn(
            'w-full px-3 py-2 rounded-lg border-2 transition-all',
            'bg-white dark:bg-gray-800',
            'text-tutara-charcoal dark:text-gray-200',
            'border-gray-300 dark:border-gray-600',
            'focus:border-tutara-turquoise focus:outline-none',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            error && 'border-red-500',
            className
          )}
          value={internalValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (filteredSuggestions.length > 0) {
              setShowSuggestions(true)
            }
          }}
          placeholder={placeholder}
          {...props}
        />
        
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'w-full px-3 py-2 text-left hover:bg-tutara-primary/10 dark:hover:bg-tutara-primary/20 transition-colors',
                  'text-tutara-charcoal dark:text-gray-200',
                  index === highlightedIndex && 'bg-tutara-primary/10 dark:bg-tutara-primary/20'
                )}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
        
        {error && (
          <p className="text-sm text-red-500 mt-1">{error}</p>
        )}
      </div>
    )
  }
)

TutaraAutocomplete.displayName = 'TutaraAutocomplete'